import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  useQueryParamContext,
  useTabContext,
  useTimeFilterContext,
} from 'modules/iterableGoals/contexts';
import { CampaignSectionType, OverviewSectionType } from 'modules/iterableGoals/types';
import { getSectionState } from 'modules/iterableGoals/ducks/selectors';
import { selectIsIterableGoalsStretchFlagOn } from 'core/userContext';

type TabSectionPropType = {
  tabSectionList: OverviewSectionType[] | CampaignSectionType[];
  reFetchGoalAnalyticsData?: () => void;
};

const defaultLoadingState = true;
const defaultErrorState = false;

export const TabSections = ({ tabSectionList, reFetchGoalAnalyticsData }: TabSectionPropType) => {
  const isIterableGoalsStretchFlagOn = useSelector(selectIsIterableGoalsStretchFlagOn);
  const { selectedTab, sections, setLoadingState, setErrorState } = useTabContext();
  const { goalName } = useQueryParamContext();
  const {
    timeParams: { startTime, endTime },
  } = useTimeFilterContext();

  const tabMarkup = tabSectionList.map(({ SectionWrapper, SectionComponent, section }) => {
    const setSectionLoadingState = (value: boolean) => setLoadingState(section, value);
    const setSectionErrorState = (value: boolean) => setErrorState(section, value);
    const sectionLoadingState = sections[section]?.loading ?? defaultLoadingState;
    const sectionErrorState = sections[section]?.error ?? defaultErrorState;

    // Note: We expect the loading and error state to be present in the
    // redux state of each section. For the automatic section state update to work.
    const { loading: reduxSectionLoadingState, error: reduxSectionErrorState } =
      useSelector((state: any) => getSectionState(state)(section)) || {};

    if (typeof reduxSectionLoadingState !== 'undefined') {
      /**
       * Sync the context state of the sections with the
       * redux loading states of the sections
       */
      useEffect(() => {
        setSectionLoadingState(reduxSectionLoadingState);
      }, [reduxSectionLoadingState]);

      useEffect(() => {
        setSectionErrorState(reduxSectionErrorState);
      }, [reduxSectionErrorState]);
    } else {
      /**
       * If the redux state is undefined then we just want
       * to show the section content on page load.
       *
       * We want to reset back the loading state whenever time filter
       * changes the section's state.
       */
      useEffect(() => {
        setSectionLoadingState(false);
      }, [sectionLoadingState]);
    }

    return (
      <SectionWrapper key={section} data-test={`${section}-section-wrapper`}>
        <SectionComponent
          loading={sectionLoadingState}
          error={sectionErrorState}
          isIterableGoalsStretchFlagOn={isIterableGoalsStretchFlagOn}
          currentTab={selectedTab}
          section={section}
          goalName={goalName}
          startTime={startTime}
          endTime={endTime}
          setLoadingState={setSectionLoadingState}
          setErrorState={setSectionErrorState}
          reFetchGoalAnalyticsData={reFetchGoalAnalyticsData}
        />
      </SectionWrapper>
    );
  });

  return <>{tabMarkup}</>;
};
